/**
 * The dancer colour palette, and the contrast maths that make a dancer's own
 * colour safe to print words in.
 *
 * WHY THIS FILE EXISTS SEPARATELY FROM familyView.ts: the palette used to live
 * there, next to hooks that import React, Firestore and the auth context.
 * Nothing that imports Firestore can be run directly by a node test, and the
 * dancer home now depends on this maths being right on a near-black band where
 * DESIGN.md's measured contrast table says several bright tokens are
 * "never text". So the data and the maths moved here, into a module that
 * imports NOTHING, and familyView.ts re-exports every old name so no call site
 * had to change. One definition, two doors.
 *
 * Bobbie, 2026-08-16: *"The other session had made kids be able to select
 * their [own] color for their view which is fun and [the younger] kids look
 * good but more colorful"*. A dancer's picked colour is therefore load-bearing
 * on their home page, including in the hero, where it has to read as WORDS on
 * an always-dark band. `accentOnDark` is the guarantee that it always does.
 *
 * NOTHING in this file may import a local module: Node's type stripping does
 * not run the bundler's resolver, and tests/dancer-home.test.mjs runs this
 * file directly. It imports nothing.
 */

/**
 * Tuned bright-but-readable palette. Each entry is (bg, fg, border) so
 * the consumer can build chips, calendar blocks, profile headers all
 * with the same color. The set is deliberately distinct enough to scan
 * 4-5 kids on the same screen without confusion.
 */
const KID_PALETTE: { name: KidColorName; bg: string; fg: string; border: string }[] = [
  { name: 'rose', bg: '#FFE1E9', fg: '#8B1A36', border: '#FF6B8A' },
  { name: 'tangerine', bg: '#FFF1E0', fg: '#7A4A00', border: '#FFB14B' },
  { name: 'lime', bg: '#EAF7D9', fg: '#3A6700', border: '#A8E63A' },
  { name: 'sky', bg: '#E0F2FF', fg: '#0C5A82', border: '#5EC8FF' },
  { name: 'lavender', bg: '#EFE6FF', fg: '#3F1E80', border: '#B59CFF' },
  { name: 'mint', bg: '#D9F5E8', fg: '#0E5B3C', border: '#7AE0B8' },
  { name: 'coral', bg: '#FFEAEA', fg: '#8B1A36', border: '#FF8B8B' },
  { name: 'honey', bg: '#FFF3CC', fg: '#7a5400', border: '#FFD86E' },
];

/**
 * The colors a dancer can be given. Stored on the student as a NAME rather
 * than raw hex, so a chosen color keeps working if the palette is ever
 * retuned, and nobody can set an unreadable combination by hand.
 */
export type KidColorName =
  | 'rose'
  | 'tangerine'
  | 'lime'
  | 'sky'
  | 'lavender'
  | 'mint'
  | 'coral'
  | 'honey';

/** Every choosable color, for a picker. */
export const KID_COLOR_CHOICES: { name: KidColorName; label: string; bg: string; border: string }[] =
  KID_PALETTE.map((c) => ({
    name: c.name,
    label: c.name.charAt(0).toUpperCase() + c.name.slice(1),
    bg: c.bg,
    border: c.border,
  }));

/** Every colour name, in palette order. The younger view rotates through it. */
export const KID_COLOR_NAMES: KidColorName[] = KID_PALETTE.map((c) => c.name);

export function isKidColorName(v: unknown): v is KidColorName {
  return typeof v === 'string' && KID_PALETTE.some((c) => c.name === v);
}

/** djb2 string hash -> unsigned int. Tiny and deterministic. */
function djb2(s: string): number {
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash) + s.charCodeAt(i);
  }
  return hash >>> 0;
}

export interface KidColor {
  bg: string;
  fg: string;
  border: string;
}

/**
 * Color for a dancer. A color they have been given wins; otherwise we fall
 * back to the original deterministic pick, so every dancer still has a stable
 * color before anyone chooses one and nothing changes for existing families.
 */
export function colorForStudentId(id: string, chosen?: string | null): KidColor {
  if (isKidColorName(chosen)) {
    const picked = KID_PALETTE.find((c) => c.name === chosen);
    if (picked) return picked;
  }
  const idx = djb2(id) % KID_PALETTE.length;
  return KID_PALETTE[idx];
}

/** The palette name a dancer effectively has, chosen or deterministic. */
export function colorNameForStudentId(id: string, chosen?: string | null): KidColorName {
  if (isKidColorName(chosen)) return chosen;
  return KID_PALETTE[djb2(id) % KID_PALETTE.length].name;
}

/** Neutral palette entry for "studio-wide" / unaffiliated calendar blocks. */
export const STUDIO_COLOR: KidColor = {
  bg: '#eeeeee',
  fg: '#1a1a1a',
  border: '#bbb',
};

// ---------------------------------------------------------------------------
// Contrast, measured rather than assumed
// ---------------------------------------------------------------------------

/**
 * The LIGHTEST point of the command center hero band. `--ticker-bg` is a
 * gradient (#0f1211 -> #1a1e1c -> #0f1211 in light mode, #101112 -> #191b1c in
 * dark), so the honest surface to measure against is its lightest stop: a
 * colour that clears the bar there clears it everywhere on the band.
 */
export const HERO_DARK = '#1a1e1c';

/** WCAG 2.1 body-text bar. Large text needs only 3.0, but the counters, the
 *  ticker and the greeting all print real words, so everything here is held
 *  to the body bar rather than the generous one. */
export const CONTRAST_BODY = 4.5;

function parseHex(hex: string): [number, number, number] {
  const clean = hex.replace('#', '').trim();
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function toHex(rgb: [number, number, number]): string {
  return `#${rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
}

/** WCAG 2.1 relative luminance. The same formula DESIGN.md's measured light
 *  mode table was built with, so the two agree by construction. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const light = Math.max(la, lb);
  const dark = Math.min(la, lb);
  return (light + 0.05) / (dark + 0.05);
}

/** Mix `hex` toward white by `amount` (0 keeps it, 1 is pure white). */
function towardWhite(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  return toHex([
    r + (255 - r) * amount,
    g + (255 - g) * amount,
    b + (255 - b) * amount,
  ]);
}

/**
 * `hex` if it already reads as words on `surface`, otherwise the same colour
 * TINTED toward white in small steps until it does. Tinting rather than
 * substituting matters: a dancer who picked coral should still see coral, just
 * a legible one. Pure white is the last resort.
 *
 * This is the DESIGN.md contrast rule running in the other direction: that
 * measured table warns that bright tokens fail as text on LIGHT surfaces. On a
 * near-black band the failures are the DARK ones, and the same formula catches
 * them. Exported because it is the guarantee, not an implementation detail:
 * tests/dancer-home.test.mjs proves it rescues a deliberately unreadable
 * colour, which is what keeps a future palette retune from quietly shipping a
 * dancer a name they cannot read.
 */
export function tintUntilReadable(hex: string, surface: string, minRatio = CONTRAST_BODY): string {
  if (contrastRatio(hex, surface) >= minRatio) return hex;
  for (let step = 1; step <= 20; step++) {
    const tinted = towardWhite(hex, step / 20);
    if (contrastRatio(tinted, surface) >= minRatio) return tinted;
  }
  return '#ffffff';
}

/**
 * A dancer's colour, made safe to print words in on the always-dark hero.
 *
 * The palette's `border` value is the bright half of each colour and is the
 * intended personal accent. All eight clear 4.5:1 on the band today, measured,
 * so the tint below is a guard rather than a routine step. An unknown or unset
 * colour falls back to studio green, which clears the band at 7.72:1.
 */
export function accentOnDark(name: KidColorName | null | undefined): string {
  const entry = (name && KID_PALETTE.find((c) => c.name === name)) || null;
  return tintUntilReadable(entry ? entry.border : '#42C92F', HERO_DARK);
}

/**
 * The same colour for words on a LIGHT card: the palette's `fg`, which was
 * tuned dark on purpose. Studio green is not a fallback here - DESIGN.md
 * measures it at 2.03:1 on white and calls it "never text" - so an unknown
 * name falls back to the app's ink token instead.
 */
export function accentOnLight(name: KidColorName | null | undefined): string {
  const entry = (name && KID_PALETTE.find((c) => c.name === name)) || null;
  return entry ? entry.fg : 'var(--ink)';
}

/** The soft wash and hairline for a section card in that colour. */
export function washForName(name: KidColorName | null | undefined): { bg: string; border: string; text: string } {
  const entry = (name && KID_PALETTE.find((c) => c.name === name)) || KID_PALETTE[0];
  return { bg: entry.bg, border: entry.border, text: entry.fg };
}

/**
 * The younger view's rotation: the dancer's OWN colour first, then the rest of
 * the palette in order, so their page still opens in their colour and then
 * gets, in Bobbie's word, more colorful. Deterministic, so the same dancer
 * sees the same card in the same colour on every load.
 */
export function rotationFrom(name: KidColorName | null | undefined): KidColorName[] {
  const start = name && isKidColorName(name) ? KID_COLOR_NAMES.indexOf(name) : 0;
  const at = start < 0 ? 0 : start;
  return KID_COLOR_NAMES.map((_, i) => KID_COLOR_NAMES[(at + i) % KID_COLOR_NAMES.length]);
}

/**
 * N18: the props that let a person's own home wear their chosen colour.
 *
 * Returns `{}` when no (valid) colour is chosen, so a person who never picked
 * one sees today's home exactly. Otherwise a class plus three custom
 * properties: the text shade for a LIGHT page (the palette's dark `fg`), the
 * text shade for a DARK page (`accentOnDark`, tinted until it clears 4.5:1),
 * and the bright `border` for hairlines (decoration only, never text).
 * theme.css picks light or dark under `.home-accent`; the styles read
 * `--home-accent-text` with their old colour as the fallback. Colour only, so
 * nothing can shift the layout.
 */
export function homeAccentProps(
  name: string | null | undefined,
): { className?: string; style?: Record<string, string> } {
  if (!isKidColorName(name)) return {};
  const entry = KID_PALETTE.find((c) => c.name === name);
  if (!entry) return {};
  return {
    className: 'home-accent',
    style: {
      '--home-accent-light': tintUntilReadable(entry.fg, '#ffffff'),
      '--home-accent-dark': accentOnDark(name),
      '--home-accent-line': entry.border,
    },
  };
}

/**
 * Which colour a person's home wears (N18), one answer for every sign-in.
 *
 * A Jr Staff is ONE PERSON (CLAUDE.md): her dancer sign-in wears
 * `colorNameForStudentId(student.id, student.color)`, so her email sign-in,
 * once it is linked to that student, resolves to exactly that, the
 * deterministic pick included. Until the student record has been read it
 * answers null (no accent) rather than flash her own `profileColor` and then
 * change; if the read failed, or nobody is linked, `profileColor` is used.
 */
export function homeAccentName(input: {
  profileColor?: string | null;
  studentId?: string | null;
  studentColor?: string | null;
  studentLoaded?: boolean;
  studentFailed?: boolean;
}): KidColorName | null {
  const own = isKidColorName(input.profileColor) ? input.profileColor : null;
  const sid = typeof input.studentId === 'string' ? input.studentId.trim() : '';
  if (!sid) return own;
  if (input.studentLoaded) return colorNameForStudentId(sid, input.studentColor);
  return input.studentFailed ? own : null;
}
